import { useCallback, useEffect, useState } from "react";
import { startRegistration, type PublicKeyCredentialCreationOptionsJSON } from "@simplewebauthn/browser";
import { useAction } from "convex/react";
import { Fingerprint, KeyRound, ShieldCheck, Trash2 } from "lucide-react";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import { Button } from "../ui/button";
import { Dialog } from "../ui/Dialog";
import { errorMessage } from "./format";
import { detectPasskeyPlatform, passkeySettingsNickname } from "../auth/passkeyPlatform";

type Passkey = { id: Id<"passkeyCredentials">; nickname: string; createdAt: number; lastUsedAt?: number; backedUp: boolean };

export function AccountSecurityDialog({ onClose }: { onClose: () => void }) {
  const passkeyPlatform = detectPasskeyPlatform(navigator.userAgent);
  const windowsPasskey = passkeyPlatform === "windows";
  const begin = useAction(api.passkeys.beginRegistration);
  const finish = useAction(api.passkeys.finishRegistration);
  const load = useAction(api.passkeys.listMine);
  const revoke = useAction(api.passkeys.revokeMine);
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refresh = useCallback(async () => { setPasskeys(await load({})); }, [load]);
  useEffect(() => { void refresh().catch(() => { setError("We couldn’t load your passkeys."); }); }, [refresh]);

  const add = async () => {
    setBusy(true); setError(null);
    try {
      if (!("PublicKeyCredential" in window)) throw new Error("This device does not support passkeys.");
      const request = await begin({});
      const response = await startRegistration({ optionsJSON: JSON.parse(request.optionsJson) as PublicKeyCredentialCreationOptionsJSON });
      const nickname = passkeySettingsNickname(passkeyPlatform, navigator.userAgent);
      await finish({ flowId: request.flowId, responseJson: JSON.stringify(response), nickname });
      await refresh();
    } catch (caught) {
      const message = errorMessage(caught);
      const domainMismatch = /securityerror|relying party|rp id|domain/iu.test(message);
      setError(message.includes("NotAllowedError")
        ? windowsPasskey
          ? "Windows Hello setup was canceled. Try again, then choose Sign-in options and PIN."
          : "Passkey setup was canceled or timed out."
        : domainMismatch
          ? "Yodl could not confirm this website for passkey setup. Reload https://phiclaw.ai/yodl/ and try again."
          : "We couldn’t add a passkey on this device. Use Safari or Chrome, then try again.");
    }
    finally { setBusy(false); }
  };

  return (
    <Dialog title="Account security" onClose={onClose}>
      <div className="stack account-security">
        <div className="settings-hero"><span className="settings-hero__icon">{windowsPasskey ? <KeyRound size={22} /> : <Fingerprint size={22} />}</span><div><strong>{windowsPasskey ? "Windows Hello PIN and passkeys" : "Touch ID and passkeys"}</strong><p>{windowsPasskey ? "Use your Windows Hello PIN to protect passkeys on this PC. Yodl never receives the PIN and stores only the public key needed to verify you." : "A passkey uses your fingerprint, face, or device PIN. Your biometric never leaves your device, and Yodl stores only the public key needed to verify you."}</p></div></div>
        <div className="passkey-list">
          {passkeys.map((passkey) => <div key={passkey.id}><ShieldCheck size={17} /><div><strong>{passkey.nickname}</strong><span>Added {new Date(passkey.createdAt).toLocaleDateString()}{passkey.backedUp ? " · securely synced by your device" : ""}</span></div><Button size="icon" variant="ghost" aria-label={`Remove ${passkey.nickname}`} onClick={() => { if (window.confirm(`Remove ${passkey.nickname}?`)) void revoke({ credentialId: passkey.id }).then(refresh); }}><Trash2 size={15} /></Button></div>)}
          {passkeys.length === 0 ? <p className="form-notice">No passkeys yet. Your password continues to work until you add one.</p> : null}
        </div>
        {error !== null ? <p className="form-error" role="alert">{error}</p> : null}
        <div className="dialog__actions dialog__actions--spread"><span className="nav-hint">Add more than one device so you always have a backup.</span><Button variant="primary" disabled={busy} onClick={() => { void add(); }}>{windowsPasskey ? <KeyRound size={16} /> : <Fingerprint size={16} />} {busy ? "Waiting for your device…" : windowsPasskey ? "Add Windows Hello PIN passkey" : "Add Touch ID or passkey"}</Button></div>
      </div>
    </Dialog>
  );
}
